import { useLocation } from '../../../features/locations/use-location'
import { useNetworkOverview } from '../../../features/locations/use-network-overview'
import { useIdOrCode } from '../../use-id-or-code'
import { Facts, Figure, Header, LedgerLink, locPath, mono, Note, Section, SkeletonRows, Status } from './ui'

export function LedgerLocationDetail() {
  const idOrCode = useIdOrCode()
  const { data: loc, isLoading, error } = useLocation(idOrCode)
  const net = useNetworkOverview(idOrCode)

  if (isLoading) return <SkeletonRows />
  if (error) return <Note tone="error">This entry could not be read: {error.message}</Note>
  if (!loc) return <Note>No location on record matches &ldquo;{idOrCode}&rdquo;.</Note>

  const profile = loc.networkProfiles?.[0] // Drizzle relations are arrays
  const code = loc.aptosStoreCode ?? loc.code
  const address = [loc.addressLine1, [loc.addressCity, loc.addressState].filter(Boolean).join(', '), loc.addressZipCode]
    .filter(Boolean)
    .join(', ')
  const standfirst = [
    loc.banner?.toUpperCase(),
    loc.region && `Region ${loc.region}`,
    loc.district && `District ${loc.district}`,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <article>
      <Header
        eyebrow={
          <>
            <LedgerLink to="/locations">Locations</LedgerLink> / Overview
          </>
        }
        title={
          <>
            <span className="text-[var(--color-muted)]" style={mono}>
              {code}
            </span>{' '}
            {loc.name}
          </>
        }
        standfirst={standfirst || undefined}
        aside={<Status label={loc.status ? 'Active' : 'Inactive'} />}
      />

      <Section title="Key facts" note={address || undefined}>
        <Facts
          items={[
            ['Banner', loc.banner?.toUpperCase()],
            ['Region', loc.region],
            ['District', loc.district],
            ['Type', loc.locationTypeLabel],
            [
              'Store code',
              <span key="c" style={mono}>
                {code}
              </span>,
            ],
            ['Terminals', loc.terminals ? String(loc.terminals.length) : null],
          ]}
        />
      </Section>

      <Section title="Network profile">
        {profile ? (
          <Facts
            items={[
              ['Lifecycle', profile.lifecycleState],
              ['Edge', profile.edgeVendor],
              ['Switching', profile.switchVendor],
              ['Cloud gateway', profile.cgVendor],
              [
                'Primary gateway',
                profile.ipPrimaryGw ? (
                  <span key="g" style={mono}>
                    {String(profile.ipPrimaryGw)}
                  </span>
                ) : null,
              ],
              [
                'Primary CIDR',
                profile.ipPrimaryCidr ? (
                  <span key="p" style={mono}>
                    {String(profile.ipPrimaryCidr)}
                  </span>
                ) : null,
              ],
            ]}
          />
        ) : (
          <Note>No network profile has been entered for this store.</Note>
        )}
      </Section>

      <Section
        title="Device status"
        aside={<LedgerLink to={`${locPath(idOrCode)}/network`}>Read the network report &rarr;</LedgerLink>}
      >
        {net.isLoading ? (
          <SkeletonRows n={2} />
        ) : net.data ? (
          <div className="grid grid-cols-2 gap-y-6 border-b border-[var(--color-border)] py-6 sm:grid-cols-4">
            <Figure label="Devices" value={net.data.summary.total} />
            <Figure label="Up" value={net.data.summary.up} tone="ok" />
            <Figure label="Down" value={net.data.summary.down} tone={net.data.summary.down > 0 ? 'bad' : undefined} />
            <Figure label="Other" value={net.data.summary.other} />
          </div>
        ) : (
          <Note>{net.error ? 'Device status is unavailable at present.' : 'No device status has been reported.'}</Note>
        )}
      </Section>
    </article>
  )
}
